import {
  BaseTextFieldProps,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  CountryData,
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';
import { useTranslation } from 'react-i18next';

export interface PhoneInputProps extends BaseTextFieldProps {
  value: string;
  defaultCountry?: string;
  defaultCountriesArray?: CountryData[];
  label?: string;
  disableCountryGuess?: boolean;
  forceDialCode?: boolean;
  onChange: (phone: string) => void;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  defaultCountry = 'us',
  defaultCountriesArray = defaultCountries,
  label,
  disableCountryGuess = false,
  forceDialCode = false,
  onChange,
  ...restProps
}) => {
  const { t } = useTranslation();
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry,
      value,
      countries: defaultCountriesArray,
      disableCountryGuess,
      forceDialCode,
      onChange: (data) => {
        onChange(data.phone);
      },
    });

  return (
    <TextField
      variant="outlined"
      label={label ?? t('regPhone')}
      color="primary"
      placeholder={label ?? (t('regPhone') || '')}
      value={inputValue}
      onChange={handlePhoneValueChange}
      type="tel"
      inputRef={inputRef}
      InputProps={{
        startAdornment: (
          <InputAdornment
            position="start"
            style={{ marginRight: '2px', marginLeft: '-8px' }}
          >
            <Select
              MenuProps={{
                style: {
                  height: '300px',
                  width: 'auto',
                  top: '10px',
                  left: '-34px',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              sx={{
                width: 'max-content',
                fieldset: {
                  display: 'none',
                },
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: 'block',
                  },
                },
                '.MuiSelect-select': {
                  padding: '8px',
                  paddingRight: '24px !important',
                },
                svg: {
                  right: 0,
                },
              }}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(value1) => (
                <FlagImage iso2={value1} style={{ display: 'flex' }} />
              )}
            >
              {defaultCountriesArray.map((c) => {
                const parsedCountry = parseCountry(c);
                return (
                  <MenuItem key={parsedCountry.iso2} value={parsedCountry.iso2}>
                    <FlagImage
                      iso2={parsedCountry.iso2}
                      style={{ marginRight: '8px', marginLeft: 0 }}
                    />
                    <Typography marginRight="8px">
                      {parsedCountry.name}
                    </Typography>
                    <Typography color="gray">
                      +{parsedCountry.dialCode}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        ),
      }}
      {...restProps}
    />
  );
};
